import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Check,
  X,
  Pencil,
  Loader2,
  FileSpreadsheet,
  Unlink,
  Combine,
} from 'lucide-react';
import { Pager } from '../../components/Pager';
import {
  useMappingsList,
  useTaxonomyList,
  SECTOR_TAXONOMY_QK,
} from './useSectorTaxonomy';
import { MappingEditModal } from './MappingEditModal';
import { BulkMergeModal } from './BulkMergeModal';
import { MappingResetModal } from './MappingResetModal';
import { useAuthStore } from '../../stores/authStore';
import {
  ALL_MAPPING_STATUSES,
  ALL_TAXONOMY_SOURCES,
  DEFAULT_MAPPINGS_FILTERS,
  formatMappingStatus,
  formatTaxonomySource,
  type MappingsFilterState,
} from './sectorTaxonomy.types';
import {
  approveMapping,
  bulkApproveMappings,
  bulkRejectMappings,
  getRebalanceRun,
  listMappings,
  listRebalanceRuns,
  rejectMapping,
  unmergeMapping,
  type SectorMappingItem,
  type MappingStatus,
  type TaxonomySource,
} from '../../api/sectorTaxonomyApi';
import { downloadPendingMappingsXlsx } from './pendingMappingsExcel';
import { InfoTooltip } from '../../components/InfoTooltip';
import { TX_TOOLTIPS } from './tooltipTexts';

interface Props {
  pushToast: (msg: string, tone?: 'info' | 'success' | 'error' | 'warn') => void;
}

// 탭 B — 원천 ↔ 정규 매핑 테이블. 1:N 편집 / 승인·반려 / 일괄 승인.
export function MappingsTab({ pushToast }: Props): JSX.Element {
  const [filters, setFilters] = useState<MappingsFilterState>(DEFAULT_MAPPINGS_FILTERS);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [pendingRowIds, setPendingRowIds] = useState<Set<number>>(new Set());
  const [editTarget, setEditTarget] = useState<SectorMappingItem | null>(null);
  const [rejectTarget, setRejectTarget] = useState<SectorMappingItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  // unmerge 모달 상태 — confirmed 매핑의 [해제] 액션.
  // reason 필수, force=true 는 마지막 confirmed 매핑일 때 orphan 경고에 대한 덮어쓰기.
  const [unmergeTarget, setUnmergeTarget] = useState<SectorMappingItem | null>(null);
  const [unmergeReason, setUnmergeReason] = useState('');
  const [unmergeForce, setUnmergeForce] = useState(false);
  const [unmergeBusy, setUnmergeBusy] = useState(false);
  const [unmergeOrphanWarn, setUnmergeOrphanWarn] = useState(false);
  // 대기 전체 승인/반려 확인 모달 상태. null 이면 닫힘.
  const [confirmAllAction, setConfirmAllAction] = useState<'approve' | 'reject' | null>(null);
  // "대기 전체 반려" 모달에서만 노출되는 사유 입력값 (필수).
  const [confirmAllReason, setConfirmAllReason] = useState('');
  // 일괄 작업(승인/반려) 진행 중 플래그. 버튼 disable + 중복 클릭 방지용.
  const [bulkBusy, setBulkBusy] = useState(false);
  // 엑셀 내보내기 진행 중 플래그.
  const [exporting, setExporting] = useState(false);
  // 일괄 머지 모달 표시 여부 (2026-05-15 운영 결정).
  const [bulkMergeOpen, setBulkMergeOpen] = useState(false);
  // 전체 매핑 reset 모달 표시 여부 (2026-05-18 운영 결정).
  const [resetModalOpen, setResetModalOpen] = useState(false);

  // 현재 운영자 식별자 — BulkMergeModal reviewer 기본값. email 우선, 없으면 'admin'.
  const currentUserEmail = useAuthStore((s) => s.user?.email ?? null);
  const defaultReviewer = currentUserEmail ?? 'admin';

  // ── 딥링크 자동 필터 (from FeedbackTab targetRef): ?source=naver_theme&sourceName=반도체장비
  // 수신 시 기존 필터 상태(기간/status 등) 초기화하고 source/sourceName 만 세팅.
  // 소비 후 URL 쿼리는 제거하여 중복 적용 방지.
  const [searchParams, setSearchParams] = useSearchParams();
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    const sourceParam = searchParams.get('source');
    const sourceName = searchParams.get('sourceName');
    if (!sourceParam && !sourceName) return;

    const validSource = (ALL_TAXONOMY_SOURCES as string[]).includes(sourceParam ?? '')
      ? (sourceParam as (typeof ALL_TAXONOMY_SOURCES)[number])
      : null;

    setFilters({
      ...DEFAULT_MAPPINGS_FILTERS,
      sources: validSource ? [validSource] : [],
      sourceSectorName: sourceName ?? '',
    });

    consumedRef.current = true;
    const sp = new URLSearchParams(searchParams);
    sp.delete('source');
    sp.delete('sourceName');
    setSearchParams(sp, { replace: true });
  }, [searchParams, setSearchParams]);

  const qc = useQueryClient();
  const list = useMappingsList({
    page: filters.page,
    pageSize: filters.pageSize,
    source: filters.sources.length > 0 ? filters.sources : undefined,
    status: filters.statuses.length > 0 ? filters.statuses : undefined,
    canonicalSectorId: filters.canonicalSectorId || undefined,
    sourceSectorName: filters.sourceSectorName || undefined,
    minConfidence: filters.minConfidence > 0 ? filters.minConfidence : undefined,
  });
  // 엑셀 "승인 후 시뮬레이션" 시트용 canonical 목록. 화면 1회 mount 시 캐시.
  const taxonomyQuery = useTaxonomyList({ isActive: true });

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: [SECTOR_TAXONOMY_QK.mappings] });
  }, [qc]);

  const markPending = useCallback((id: number, busy: boolean) => {
    setPendingRowIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  async function handleApprove(item: SectorMappingItem): Promise<void> {
    markPending(item.mappingId, true);
    try {
      await approveMapping(item.mappingId, { note: null });
      pushToast(`매핑 ${item.mappingId} 승인됨.`, 'success');
      invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'approve_failed';
      pushToast(`승인 실패: ${msg}`, 'error');
    } finally {
      markPending(item.mappingId, false);
    }
  }

  async function handleRejectConfirm(): Promise<void> {
    if (!rejectTarget) return;
    const reason = rejectReason.trim();
    if (!reason) {
      pushToast('반려 사유를 입력하세요.', 'warn');
      return;
    }
    markPending(rejectTarget.mappingId, true);
    try {
      await rejectMapping(rejectTarget.mappingId, reason);
      pushToast(`매핑 ${rejectTarget.mappingId} 반려됨.`, 'success');
      invalidate();
      setRejectTarget(null);
      setRejectReason('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'reject_failed';
      pushToast(`반려 실패: ${msg}`, 'error');
    } finally {
      markPending(rejectTarget.mappingId, false);
    }
  }

  async function handleUnmergeConfirm(): Promise<void> {
    if (!unmergeTarget) return;
    const reason = unmergeReason.trim();
    if (!reason) {
      pushToast('해제 사유를 입력하세요.', 'warn');
      return;
    }
    setUnmergeBusy(true);
    try {
      await unmergeMapping(unmergeTarget.mappingId, {
        reason,
        force: unmergeForce || undefined,
      });
      pushToast(`매핑 ${unmergeTarget.mappingId} 해제됨.`, 'success');
      setUnmergeTarget(null);
      setUnmergeReason('');
      setUnmergeForce(false);
      setUnmergeOrphanWarn(false);
      invalidate();
    } catch (err) {
      // 422 would_orphan → force 필요 안내. 백엔드 ApiError.code 규약: 'would_orphan'.
      // 그 외는 일반 에러 토스트.
      const anyErr = err as { code?: string; message?: string; httpStatus?: number } | undefined;
      if (anyErr?.code === 'would_orphan' || anyErr?.httpStatus === 422) {
        setUnmergeOrphanWarn(true);
        pushToast('마지막 confirmed 매핑 — force=true 로 재시도 필요.', 'warn');
      } else {
        const msg = err instanceof Error ? err.message : 'unmerge_failed';
        pushToast(`해제 실패: ${msg}`, 'error');
      }
    } finally {
      setUnmergeBusy(false);
    }
  }

  async function handleBulkApprove(): Promise<void> {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const resp = await bulkApproveMappings({ mappingIds: Array.from(selected) });
      pushToast(
        `${resp.succeeded} 건 승인 완료` + (resp.failed > 0 ? ` (${resp.failed} 건 실패)` : ''),
        resp.failed > 0 ? 'warn' : 'success',
      );
      setSelected(new Set());
      invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'bulk_approve_failed';
      pushToast(`일괄 승인 실패: ${msg}`, 'error');
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleBulkReject(): Promise<void> {
    if (selected.size === 0) return;
    // 운영 감사 기록을 위해 사유는 필수. window.prompt 로 간단 수집.
    // 취소·공백 입력은 aborts.
    const raw = window.prompt('반려 사유를 입력하세요 (필수)');
    if (raw === null) return;
    const reason = raw.trim();
    if (!reason) {
      pushToast('반려 사유는 비워둘 수 없습니다.', 'warn');
      return;
    }
    setBulkBusy(true);
    try {
      const resp = await bulkRejectMappings({
        mappingIds: Array.from(selected),
        reason,
      });
      pushToast(
        `${resp.succeeded} 건 반려 완료` + (resp.failed > 0 ? ` (${resp.failed} 건 실패)` : ''),
        resp.failed > 0 ? 'warn' : 'success',
      );
      setSelected(new Set());
      invalidate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'bulk_reject_failed';
      pushToast(`일괄 반려 실패: ${msg}`, 'error');
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleDownloadPendingExcel(): Promise<void> {
    if (exporting) return;
    setExporting(true);
    try {
      // pending_review 전량. 백엔드 cap=1000 으로 확장됨 (과거 200 cap 때문에 일부
      // 매핑 누락되던 문제 해결).
      const pendingResp = await listMappings({
        page: 0,
        pageSize: 1000,
        status: ['pending_review'],
      });
      // 원본 섹터 시트용: 현재 필터와 무관하게 전체. pending + confirmed + rejected
      // 까지 다 가져와야 unique source 목록이 완전함. 1000 한도 내.
      let allResp: { items: SectorMappingItem[] } = { items: [] };
      try {
        const r = await listMappings({
          page: 0,
          pageSize: 1000,
          status: ['pending_review', 'confirmed', 'rejected'],
        });
        allResp = { items: r.items };
      } catch {
        // 실패해도 pending 만으로 시트 2 fallback — silent.
      }

      // uncovered 원천은 최신 Type-A run 의 raw_proposal.uncovered_sources 에서 획득.
      // 엑셀 시트 1/2 에 "uncovered" 행으로 추가돼 342 원천 전체 커버.
      let uncoveredSources: string[] = [];
      try {
        const runs = await listRebalanceRuns({ limit: 1 });
        const latest = runs[0];
        if (latest?.runId) {
          const detail = await getRebalanceRun(latest.runId);
          uncoveredSources = Array.isArray(detail.uncoveredSources)
            ? detail.uncoveredSources
            : [];
        }
      } catch {
        // uncovered 조회 실패 시 엑셀은 pending 만으로 생성 — silent.
      }

      await downloadPendingMappingsXlsx({
        pendingMappings: pendingResp.items,
        taxonomy: taxonomyQuery.data ?? [],
        allMappingsForSourceSheet: allResp.items,
        uncoveredSources,
      });
      pushToast(
        `대기 매핑 ${pendingResp.items.length} 건 + uncovered ${uncoveredSources.length} 원천 엑셀 다운로드.`,
        'success'
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'export_failed';
      pushToast(`엑셀 다운로드 실패: ${msg}`, 'error');
    } finally {
      setExporting(false);
    }
  }

  // 대기 전체(pending_review) 일괄 승인/반려. 모달 확인 후 호출.
  // 백엔드 bulk-approve / bulk-reject 모두 `filter.status='pending_review'` 단독으로
  // pending 전체를 대상으로 삼는다 (BULK_APPROVE_MAX=500 상한, 현 pending 325건).
  async function handleConfirmAllAction(): Promise<void> {
    const action = confirmAllAction;
    if (!action) return;

    if (action === 'reject') {
      const reason = confirmAllReason.trim();
      if (!reason) {
        pushToast('반려 사유는 비워둘 수 없습니다.', 'warn');
        return;
      }
    }

    setBulkBusy(true);
    try {
      const resp = action === 'approve'
        ? await bulkApproveMappings({ filter: { status: 'pending_review' } })
        : await bulkRejectMappings({
            filter: { status: 'pending_review' },
            reason: confirmAllReason.trim(),
          });

      const verb = action === 'approve' ? '승인' : '반려';
      if (resp.requested === 0) {
        // 백엔드 filter 조회 결과 0건 — pending_review 상태 매핑이 하나도 없음.
        // 성공이 아니라 "대상 없음" 으로 안내한다.
        pushToast(
          `대상 매핑 없음 — pending_review 상태 매핑이 0건이라 ${verb}할 항목이 없습니다.`,
          'warn',
        );
      } else {
        pushToast(
          `대기 전체 ${verb} — ${resp.succeeded} / ${resp.requested} 건 완료` +
            (resp.failed > 0 ? ` (${resp.failed} 건 실패)` : ''),
          resp.failed > 0 ? 'warn' : 'success',
        );
      }
      setConfirmAllAction(null);
      setConfirmAllReason('');
      invalidate();
    } catch (err) {
      const verb = action === 'approve' ? '승인' : '반려';
      const msg = err instanceof Error ? err.message : `bulk_${action}_failed`;
      pushToast(`일괄 ${verb} 실패: ${msg}`, 'error');
    } finally {
      setBulkBusy(false);
    }
  }

  function toggleSelect(id: number): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSource(s: TaxonomySource): void {
    const next = filters.sources.includes(s)
      ? filters.sources.filter((x) => x !== s)
      : [...filters.sources, s];
    setFilters({ ...filters, sources: next, page: 0 });
  }

  function toggleStatus(s: MappingStatus): void {
    const next = filters.statuses.includes(s)
      ? filters.statuses.filter((x) => x !== s)
      : [...filters.statuses, s];
    setFilters({ ...filters, statuses: next, page: 0 });
  }

  const rowClass = useCallback((item: SectorMappingItem): string => {
    if (item.status === 'pending_review') return 'tx-row--pending';
    if (item.status === 'rejected') return 'tx-row--rejected';
    return '';
  }, []);

  // relation enum → 짧은 한글 라벨. 전체 원어는 data-tooltip 으로 노출.
  function formatRelation(rel: string): string {
    switch (rel) {
      case 'primary_name': return '대표';
      case 'equivalent_synonym': return '동의어';
      case 'sibling_by_overlap': return '형제';
      case 'parent_in_source_hierarchy': return '상위';
      case 'child_in_source_hierarchy': return '하위';
      case 'cross_source_synonym': return '교차';
      default: return rel;
    }
  }

  const confClass = (c: number | null): string => {
    if (c === null) return '';
    if (c < 0.5) return 'tx-conf--low';
    if (c >= 0.9) return 'tx-conf--high';
    return 'tx-conf--mid';
  };

  const pendingSelectedExist = useMemo(() => {
    if (selected.size === 0) return false;
    return items.some(
      (i) => selected.has(i.mappingId) && i.status === 'pending_review'
    );
  }, [selected, items]);

  return (
    <div>
      {/* 필터 바 */}
      <div className="pca-filter">
        <div className="pca-filter__group">
          <label className="pca-filter__label">Source</label>
          <div className="pca-chip-row">
            {ALL_TAXONOMY_SOURCES.map((s) => (
              <button
                key={s}
                type="button"
                className={
                  'pca-chip' +
                  (filters.sources.includes(s) ? ' pca-chip--active' : '')
                }
                onClick={() => toggleSource(s)}
              >
                {formatTaxonomySource(s)}
              </button>
            ))}
          </div>
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label">Status</label>
          <div className="pca-chip-row">
            {ALL_MAPPING_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                className={
                  'pca-chip' +
                  (filters.statuses.includes(s) ? ' pca-chip--active' : '')
                }
                onClick={() => toggleStatus(s)}
              >
                {formatMappingStatus(s)}
              </button>
            ))}
          </div>
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label" htmlFor="mp-src-name">
            원천 섹터명
          </label>
          <input
            id="mp-src-name"
            className="pca-input"
            type="text"
            placeholder="예: 반도체"
            value={filters.sourceSectorName}
            onChange={(e) =>
              setFilters({ ...filters, sourceSectorName: e.target.value, page: 0 })
            }
          />
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label" htmlFor="mp-canon">
            정규 섹터
          </label>
          <input
            id="mp-canon"
            className="pca-input"
            type="text"
            placeholder="sec_semiconductor"
            value={filters.canonicalSectorId}
            onChange={(e) =>
              setFilters({ ...filters, canonicalSectorId: e.target.value, page: 0 })
            }
          />
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label">min confidence</label>
          <input
            type="number"
            className="pca-input"
            step={0.05}
            min={0}
            max={1}
            value={filters.minConfidence}
            onChange={(e) =>
              setFilters({
                ...filters,
                minConfidence: Number(e.target.value) || 0,
                page: 0,
              })
            }
            style={{ width: 90 }}
          />
        </div>

        <div className="pca-filter__group" style={{ marginLeft: 'auto' }}>
          <label className="pca-filter__label">&nbsp;</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
            <button
              type="button"
              className="pca-btn"
              onClick={handleDownloadPendingExcel}
              disabled={exporting}
              aria-label="대기 상태 다운로드"
              title={TX_TOOLTIPS.pendingExcel}
            >
              {exporting ? (
                <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
              ) : (
                <FileSpreadsheet size={14} strokeWidth={1.75} />
              )}
              대기 상태 다운로드
            </button>
            <button
              type="button"
              className="pca-btn pca-btn--primary"
              onClick={() => {
                setConfirmAllReason('');
                setConfirmAllAction('approve');
              }}
              disabled={bulkBusy}
            >
              대기 전체 승인
            </button>
            <button
              type="button"
              className="pca-btn"
              onClick={() => {
                setConfirmAllReason('');
                setConfirmAllAction('reject');
              }}
              disabled={bulkBusy}
            >
              대기 전체 반려
            </button>
            <button
              type="button"
              className="pca-btn pca-btn--danger"
              onClick={() => setResetModalOpen(true)}
              disabled={bulkBusy}
              title="확정(confirmed) 상태 매핑만 삭제 — pending_review/rejected/superseded 는 보존, 비가역"
              data-testid="mapping-reset-button"
            >
              확정 매핑 reset
            </button>
          </div>
        </div>
      </div>

      {/* 선택 툴바 — 1건 이상 선택 시 노출. 일괄 승인/반려는 pending 대상이 있을 때만,
          일괄 머지는 confirmed/pending 무관하게 1건 이상이면 활성 (2026-05-15 결정). */}
      {selected.size > 0 && (
        <div className="pca-filter" style={{ marginTop: 8 }}>
          <span className="pca-muted">선택: {selected.size} 건</span>
          {pendingSelectedExist && (
            <>
              <button
                type="button"
                className="pca-btn pca-btn--primary"
                onClick={handleBulkApprove}
                disabled={selected.size === 0 || bulkBusy}
              >
                {bulkBusy ? (
                  <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
                ) : (
                  <Check size={14} strokeWidth={1.75} />
                )}
                선택 일괄 승인
              </button>
              <button
                type="button"
                className="pca-btn"
                onClick={handleBulkReject}
                disabled={selected.size === 0 || bulkBusy}
                aria-label="선택 일괄 반려"
              >
                <X size={14} strokeWidth={1.75} />
                선택 일괄 반려
              </button>
            </>
          )}
          <button
            type="button"
            className="pca-btn"
            onClick={() => setBulkMergeOpen(true)}
            disabled={selected.size === 0 || bulkBusy}
            aria-label="선택 머지"
            title="선택된 매핑을 단일 canonical 로 통합"
          >
            <Combine size={14} strokeWidth={1.75} />
            선택 머지
          </button>
          <button
            type="button"
            className="pca-btn"
            onClick={() => setSelected(new Set())}
          >
            선택 해제
          </button>
        </div>
      )}

      {list.isError && (
        <div className="pca-warning-banner">
          매핑 목록 로드 실패: {(list.error as Error).message}
        </div>
      )}

      {/* 테이블 */}
      <div className="pca-table-wrap" style={{ marginTop: 12 }}>
        <table className="pca-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={
                    items.length > 0 &&
                    items.every((i) => selected.has(i.mappingId))
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelected(new Set(items.map((i) => i.mappingId)));
                    } else {
                      setSelected(new Set());
                    }
                  }}
                />
              </th>
              <th>ID</th>
              <th>Source</th>
              <th>원천 섹터명</th>
              <th>→</th>
              <th>정규 섹터</th>
              <th>weight</th>
              <th>
                merge 전략
                <InfoTooltip
                  label={TX_TOOLTIPS.mergeStrategy}
                  testId="tx-tip-merge-strategy"
                />
              </th>
              <th>
                관계
                <InfoTooltip
                  label={TX_TOOLTIPS.relation}
                  testId="tx-tip-relation"
                />
              </th>
              <th>
                유사도
                <InfoTooltip
                  label={TX_TOOLTIPS.jaccard}
                  testId="tx-tip-jaccard"
                />
              </th>
              <th>status</th>
              <th>conf</th>
              <th>reviewer</th>
              <th>action</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr>
                <td colSpan={14} className="pca-muted">
                  로딩 중…
                </td>
              </tr>
            )}
            {!list.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={14} className="pca-muted">
                  조건에 맞는 매핑이 없습니다.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.mappingId} className={rowClass(item)}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(item.mappingId)}
                    onChange={() => toggleSelect(item.mappingId)}
                    aria-label={`select mapping ${item.mappingId}`}
                  />
                </td>
                <td>{item.mappingId}</td>
                <td>{formatTaxonomySource(item.source)}</td>
                <td>{item.sourceSectorName}</td>
                <td className="pca-muted">→</td>
                <td>
                  <strong>{item.canonicalDisplayName}</strong>
                  <br />
                  <span className="tx-tree__slug">{item.canonicalSectorId}</span>
                </td>
                <td>{item.weight.toFixed(2)}</td>
                <td>
                  {item.mergeStrategy ? (
                    <span className={`tx-ms tx-ms--${item.mergeStrategy}`}>{item.mergeStrategy}</span>
                  ) : (
                    <span className="pca-muted">—</span>
                  )}
                </td>
                <td>
                  {item.relation ? (
                    <span
                      className="tx-rel"
                      data-tooltip={item.relation}
                    >
                      {formatRelation(item.relation)}
                    </span>
                  ) : (
                    <span className="pca-muted">—</span>
                  )}
                </td>
                <td style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {item.overlapRatio != null ? (
                    <span className="tx-rel__ratio">{item.overlapRatio.toFixed(2)}</span>
                  ) : (
                    <span className="pca-muted">—</span>
                  )}
                </td>
                <td>
                  <span className={`tx-status tx-status--${item.status}`}>
                    {formatMappingStatus(item.status)}
                  </span>
                </td>
                <td className={confClass(item.llmConfidence)}>
                  {item.llmConfidence !== null
                    ? item.llmConfidence.toFixed(2)
                    : '—'}
                </td>
                <td>{item.reviewer ?? '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                  {item.status === 'pending_review' && (
                    <>
                      <button
                        type="button"
                        className="pca-btn pca-btn--primary"
                        disabled={pendingRowIds.has(item.mappingId)}
                        onClick={() => handleApprove(item)}
                        aria-label={`approve ${item.mappingId}`}
                      >
                        {pendingRowIds.has(item.mappingId) ? (
                          <Loader2 size={12} strokeWidth={1.75} className="pca-spin" />
                        ) : (
                          <Check size={12} strokeWidth={1.75} />
                        )}
                        승인
                      </button>
                      <button
                        type="button"
                        className="pca-btn"
                        onClick={() => setRejectTarget(item)}
                      >
                        <X size={12} strokeWidth={1.75} />
                        반려
                      </button>
                    </>
                  )}
                  {item.status === 'confirmed' && (
                    <button
                      type="button"
                      className="pca-btn tx-row-hover-action"
                      onClick={() => {
                        setUnmergeTarget(item);
                        setUnmergeReason('');
                        setUnmergeForce(false);
                        setUnmergeOrphanWarn(false);
                      }}
                      aria-label={`unmerge ${item.mappingId}`}
                      title="확정 매핑 해제 — rejected 로 전환 + 감사 기록"
                    >
                      <Unlink size={12} strokeWidth={1.75} />
                      해제
                    </button>
                  )}
                  <button
                    type="button"
                    className="pca-btn"
                    onClick={() => setEditTarget(item)}
                    aria-label={`edit ${item.mappingId}`}
                  >
                    <Pencil size={12} strokeWidth={1.75} />
                    편집
                  </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <Pager
        page={filters.page}
        pageSize={filters.pageSize}
        total={total}
        onPageChange={(p) => setFilters({ ...filters, page: p })}
      />

      {/* 반려 모달 */}
      {rejectTarget && (
        <div className="tx-modal-backdrop" role="dialog" aria-modal="true">
          <div className="tx-modal">
            <h3 className="tx-modal__title">매핑 반려</h3>
            <div className="tx-modal__body">
              mapping_id {rejectTarget.mappingId} —{' '}
              {rejectTarget.sourceSectorName} → {rejectTarget.canonicalDisplayName}
              <br />
              반려 사유를 입력해주세요 (기록 필수).
            </div>
            <textarea
              className="pca-input"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="예: Jaccard 유사도 낮아 다른 정규 섹터로 재매핑 필요"
            />
            <div className="tx-modal__actions">
              <button
                type="button"
                className="pca-btn"
                onClick={() => {
                  setRejectTarget(null);
                  setRejectReason('');
                }}
              >
                취소
              </button>
              <button
                type="button"
                className="pca-btn pca-btn--primary"
                onClick={handleRejectConfirm}
                disabled={pendingRowIds.has(rejectTarget.mappingId)}
              >
                반려 처리
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 대기 전체 승인/반려 확인 */}
      {confirmAllAction && (
        <div className="tx-modal-backdrop" role="dialog" aria-modal="true">
          <div className="tx-modal">
            <h3 className="tx-modal__title">
              대기 전체 {confirmAllAction === 'approve' ? '승인' : '반려'}
            </h3>
            <div className="tx-modal__body">
              `status='pending_review'` 인 모든 매핑을{' '}
              {confirmAllAction === 'approve' ? '승인' : '반려'}합니다. 이
              작업은 되돌릴 수 없으며 감사 이력(`updated_at`) 에만 기록됩니다.
              최대 500건까지 처리됩니다. 계속하시겠습니까?
              {confirmAllAction === 'reject' && (
                <div style={{ marginTop: 12 }}>
                  <label
                    htmlFor="tx-bulk-reject-reason"
                    style={{ display: 'block', fontSize: 12, marginBottom: 4 }}
                  >
                    반려 사유 (필수)
                  </label>
                  <input
                    id="tx-bulk-reject-reason"
                    className="pca-input"
                    type="text"
                    value={confirmAllReason}
                    onChange={(e) => setConfirmAllReason(e.target.value)}
                    placeholder="예: Type-A 재실행 대비 전체 리셋"
                    style={{ width: '100%' }}
                    autoFocus
                  />
                </div>
              )}
            </div>
            <div className="tx-modal__actions">
              <button
                type="button"
                className="pca-btn"
                onClick={() => {
                  setConfirmAllAction(null);
                  setConfirmAllReason('');
                }}
                disabled={bulkBusy}
              >
                취소
              </button>
              <button
                type="button"
                className="pca-btn pca-btn--primary"
                onClick={handleConfirmAllAction}
                disabled={
                  bulkBusy ||
                  (confirmAllAction === 'reject' && !confirmAllReason.trim())
                }
              >
                {bulkBusy ? (
                  <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
                ) : null}
                {confirmAllAction === 'approve' ? '전체 승인 실행' : '전체 반려 실행'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 일괄 머지 모달 (2026-05-15 운영 결정) */}
      {bulkMergeOpen && (
        <BulkMergeModal
          selected={items.filter((i) => selected.has(i.mappingId))}
          defaultReviewer={defaultReviewer}
          onClose={() => setBulkMergeOpen(false)}
          onSuccess={(mergedCount, targetCanonical, skippedCount) => {
            pushToast(
              `${mergedCount}건 머지 완료, target=${targetCanonical}` +
                (skippedCount > 0 ? ` (skipped ${skippedCount})` : ''),
              'success'
            );
            setBulkMergeOpen(false);
            setSelected(new Set());
            invalidate();
          }}
          onError={(msg, tone) => pushToast(msg, tone ?? 'error')}
        />
      )}

      {/* 전체 매핑 reset 모달 (비가역) */}
      {resetModalOpen && (
        <MappingResetModal
          onClose={() => setResetModalOpen(false)}
          onSuccess={(deletedCount) => {
            pushToast(
              `확정 매핑 reset 완료 — ${deletedCount.toLocaleString('ko-KR')}건 삭제. 이제 LLM Type-A 재실행으로 새 매핑을 생성하세요.`,
              'success',
            );
            setResetModalOpen(false);
            setSelected(new Set());
            invalidate();
          }}
          onError={(msg) => pushToast(`확정 매핑 reset 실패: ${msg}`, 'error')}
        />
      )}

      {/* 편집 모달 (1:N 매핑) */}
      {editTarget && (
        <MappingEditModal
          target={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            invalidate();
            pushToast('매핑 저장됨.', 'success');
          }}
          onError={(m) => pushToast(m, 'error')}
        />
      )}

      {/* 매핑 해제 (unmerge) 모달 */}
      {unmergeTarget && (
        <div className="tx-modal-backdrop" role="dialog" aria-modal="true">
          <div className="tx-modal">
            <h3 className="tx-modal__title">매핑 해제</h3>
            <div className="tx-modal__body">
              mapping_id <strong>{unmergeTarget.mappingId}</strong> —{' '}
              {unmergeTarget.sourceSectorName} →{' '}
              {unmergeTarget.canonicalDisplayName}
              <br />
              <span className="pca-muted">
                확정 매핑을 <code>rejected</code> 로 전환하고 감사 이력에
                기록합니다. 해당 canonical 섹터가 이 매핑의 유일한 confirmed
                항목일 경우 orphan 상태가 되어 서버가 거부할 수 있습니다 — 이
                경우 force 를 체크하고 재시도하세요.
              </span>
            </div>
            {unmergeOrphanWarn && (
              <div className="pca-warning-banner">
                이 매핑은 target 의 마지막 confirmed 매핑입니다. orphan 방지
                정책에 의해 거부되었습니다. 강제 진행하려면 아래 <code>force</code>{' '}
                체크 후 다시 시도.
              </div>
            )}
            <label
              htmlFor="tx-unmerge-reason"
              style={{ display: 'block', fontSize: 12, marginBottom: 4 }}
            >
              해제 사유 (필수)
            </label>
            <textarea
              id="tx-unmerge-reason"
              className="pca-input"
              rows={3}
              value={unmergeReason}
              onChange={(e) => setUnmergeReason(e.target.value)}
              placeholder="예: 재매핑 대비 해제 — 2026-04 운영자 결정"
              autoFocus
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <input
                type="checkbox"
                checked={unmergeForce}
                onChange={(e) => setUnmergeForce(e.target.checked)}
              />
              <code>force</code> — orphan 경고 무시
            </label>
            <div className="tx-modal__actions">
              <button
                type="button"
                className="pca-btn"
                onClick={() => {
                  setUnmergeTarget(null);
                  setUnmergeReason('');
                  setUnmergeForce(false);
                  setUnmergeOrphanWarn(false);
                }}
                disabled={unmergeBusy}
              >
                취소
              </button>
              <button
                type="button"
                className="pca-btn pca-btn--primary"
                onClick={handleUnmergeConfirm}
                disabled={unmergeBusy || !unmergeReason.trim()}
              >
                {unmergeBusy ? (
                  <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
                ) : null}
                해제 실행
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
